import React, { useState, useEffect } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import { toast } from '../../../../common/toast';
import axiosInstance from '../../../../fetchConfig/axiosConfig';
import { useUser } from '../../../UserContext';
import LoadingButton from '../../../../common/LoadingButton';
import logger from '../../../../utils/logger';
import { canMarkDoNotFileScar } from '../utils/helpers.js';

const DoNotFileScarDialog = ({
  open,
  onClose,
  selectedItems,
  onAssign,
  getNegotiationObj = null
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUser();
  const eligibleCases = (selectedItems || []).filter(canMarkDoNotFileScar);

  useEffect(() => {
    if (!open) setIsLoading(false);
  }, [open]);

  const handleAssign = async () => {
    if (!getNegotiationObj) {
      toast.error('Negotiation object not available for flag assignment');
      return;
    }
    if (eligibleCases.length === 0) {
      toast.error('Do not file at SCAR is only available for cases that are BAR filed and not yet filed at SCAR.');
      return;
    }

    const flagData = {
      parcelIds: eligibleCases.map(caseItem => caseItem.parcel_id),
      taxYear: getNegotiationObj.TaxYear,
      muniCode: getNegotiationObj.MuniCode,
      assigner: user?.userId || null,
      assignmentDate: new Date().toISOString().split('T')[0],
      flagType: 'DoNotFileSCAR',
      villageRun: getNegotiationObj.villageRun || 0,
    };

    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/flag-case', flagData);
      if (!response.data.success) {
        toast.error(response.data.message || response.data.error || 'Failed to flag cases');
        return;
      }
      const processedCount = eligibleCases.length;
      const totalCount = selectedItems?.length || 0;
      let toastMessage = `${processedCount} case${processedCount > 1 ? 's' : ''} marked to not file at SCAR`;
      if (processedCount < totalCount) {
        const skippedCount = totalCount - processedCount;
        toastMessage += ` (${skippedCount} case${skippedCount > 1 ? 's' : ''} were not BAR filed and awaiting SCAR)`;
      }
      toast.success(toastMessage);
      onAssign(null, null, flagData);
      onClose();
    } catch (error) {
      logger.error('Error flagging cases:', error);
      toast.error(error?.response?.data?.error || 'Failed to flag cases');
    } finally {
      setIsLoading(false);
    }
  };

  const count = eligibleCases.length;
  const caseWord = count === 1 ? 'case' : 'cases';

  return (
    <StableMuiDialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Do Not File at SCAR</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {count
            ? `${count} selected ${caseWord} ${count === 1 ? 'is' : 'are'} BAR filed and not yet filed at SCAR. ${count === 1 ? 'It' : 'They'} will be marked BAR only and will not proceed to SCAR filing.`
            : 'Do not file at SCAR is only available for cases that are BAR filed and not yet filed at SCAR.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <LoadingButton
          onClick={handleAssign}
          loading={isLoading}
          loadingText="Updating..."
          variant="contained"
          color="primary"
          disabled={count === 0}
        >
          Do Not File SCAR
        </LoadingButton>
      </DialogActions>
    </StableMuiDialog>
  );
};

export default DoNotFileScarDialog;
